import { matchesCompanyFilter, type CompanyFilter } from './companyFilter.ts';
import type { Company } from './types.ts';

export type ExpiringStatusFilter = 'all' | 'expired' | 'expiring';

export type ExpiringFilters = {
  /** Existing contractor / cluster / station dimensions. */
  company: CompanyFilter;
  /** Free-text over file name, vendor name and question label. */
  query: string;
  /** Selected vendor company ids; [] = all vendors. */
  vendorIds: string[];
  /** Which expiry section(s) to show. The page applies this at the section level. */
  status: ExpiringStatusFilter;
  /** Inclusive expiry-date range (date-input values, '' = unbounded). */
  expiryFrom: string;
  expiryTo: string;
};

/** Minimal structural row shape this module needs — the page's ExpiringRow
 *  satisfies it. */
export type FilterableExpiringRow = {
  file_name: string;
  expires_at: string;
  submission: {
    company_id: string;
    company: { id: string; company_name: string; email: string } | null;
    question: { label: string } | null;
  } | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function companyDimsActive(f: CompanyFilter): boolean {
  return f.contractorTypes.length > 0 || f.clusterRegions.length > 0 || f.stations.length > 0;
}

/**
 * Filter expiring/expired document rows by query, vendor set, expiry range, and
 * the company dimensions (contractor type / cluster region / station). The
 * `status` field is NOT applied here — the page decides which sections to render
 * from it. The "to" bound is inclusive of the entire selected day.
 */
export function filterExpiringRows<T extends FilterableExpiringRow>(
  rows: T[],
  filters: ExpiringFilters,
  companyById: Map<string, Company>,
): T[] {
  const q = filters.query.trim().toLowerCase();
  const vendorSet = new Set(filters.vendorIds);
  const fromMs = filters.expiryFrom ? Date.parse(filters.expiryFrom) : null;
  const toMs = filters.expiryTo ? Date.parse(filters.expiryTo) + DAY_MS - 1 : null;
  const dimsActive = companyDimsActive(filters.company);

  return rows.filter((row) => {
    const companyId = row.submission?.company_id ?? row.submission?.company?.id ?? null;

    // Company-dimension filter (only when at least one dimension is active).
    if (dimsActive) {
      if (!companyId) return false;
      const company = companyById.get(companyId);
      if (!company || !matchesCompanyFilter(company, filters.company)) return false;
    }

    // Vendor filter.
    if (vendorSet.size > 0 && !(companyId && vendorSet.has(companyId))) return false;

    // Expiry range.
    if (fromMs !== null || toMs !== null) {
      const t = Date.parse(row.expires_at);
      if (Number.isNaN(t)) return false;
      if (fromMs !== null && t < fromMs) return false;
      if (toMs !== null && t > toMs) return false;
    }

    // Free-text query.
    if (q) {
      const name = row.submission?.company?.company_name.toLowerCase() ?? '';
      const label = row.submission?.question?.label.toLowerCase() ?? '';
      const file = row.file_name.toLowerCase();
      if (!(file.includes(q) || name.includes(q) || label.includes(q))) return false;
    }

    return true;
  });
}

/** Distinct vendors present in the rows, sorted by name — for the Vendor filter. */
export function distinctVendors(
  rows: FilterableExpiringRow[],
): { id: string; name: string; email: string }[] {
  const byId = new Map<string, { id: string; name: string; email: string }>();
  for (const r of rows) {
    const c = r.submission?.company;
    if (c && !byId.has(c.id)) {
      byId.set(c.id, { id: c.id, name: c.company_name, email: c.email });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
